import { useEffect, useRef, useState } from 'react';

/**
 * Shared in-app confirmation modal.
 *
 * Replaces every `window.confirm(...)` / `window.alert(...)` across
 * the dashboard.  Native dialogs render outside Spotlight's design
 * system, can't be dismissed with our chrome, and (per the user's
 * feedback) read as "this app is using browser primitives because it
 * couldn't be bothered".
 *
 * Two surfaces:
 *
 *   <ConfirmDialog ... />
 *     A controlled modal.  Pass `open`, `onCancel`, `onConfirm`,
 *     and a busy/error pair if the confirm action is async.
 *
 *   useConfirm()
 *     A hook returning `[node, ask]` so route components can call
 *     `await ask({ title, body, ... })` from any handler without
 *     threading state through every row component.  Resolves
 *     `true` on confirm, `false` on cancel.  The element returned
 *     in the first slot must be rendered somewhere in the tree.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  busy = false,
  errorMessage = '',
  onCancel,
  onConfirm,
}) {
  const confirmBtnRef = useRef(null);

  // Esc closes; click on backdrop closes; focus the confirm button on open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onCancel?.();
    };
    document.addEventListener('keydown', onKey);
    // Defer focus by one paint so the button exists in the DOM.
    const t = window.setTimeout(() => confirmBtnRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      className="confirm-dialog__backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      onClick={() => { if (!busy) onCancel?.(); }}
    >
      <div
        className="confirm-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-dialog__head">
          <h3 id="confirm-dialog-title">{title}</h3>
        </div>
        <div className="confirm-dialog__body">
          {typeof body === 'string'
            ? body.split('\n').map((line, i) => <p key={i}>{line}</p>)
            : body}
          {errorMessage && (
            <div className="confirm-dialog__error" role="alert">
              {errorMessage}
            </div>
          )}
        </div>
        <div className="confirm-dialog__foot">
          <button
            type="button"
            className="btn"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            className={`btn ${danger ? 'btn--danger' : 'btn--primary'}`}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
        <style>{CONFIRM_DIALOG_CSS}</style>
      </div>
    </div>
  );
}

/**
 * Imperative confirm hook.
 *
 *   const [confirmNode, ask] = useConfirm();
 *   ...
 *   if (await ask({ title: 'Revoke key?', confirmLabel: 'Revoke' })) {
 *     ...
 *   }
 *
 *   return (<>{ ... }{confirmNode}</>);
 *
 * The hook owns the modal state.  Pass `runOnConfirm` in the ask
 * options when you want the modal to keep the busy state visible
 * while the action runs, surfacing any error inline before the
 * promise resolves.
 */
export function useConfirm() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  function ask(opts) {
    return new Promise((resolve) => {
      setBusy(false);
      setErrorMessage('');
      setState({
        ...opts,
        _resolve: resolve,
      });
    });
  }

  async function handleConfirm() {
    if (!state) return;
    if (typeof state.runOnConfirm === 'function') {
      setBusy(true);
      setErrorMessage('');
      try {
        await state.runOnConfirm();
        state._resolve(true);
        setState(null);
      } catch (e) {
        setErrorMessage(e?.message || String(e));
      } finally {
        setBusy(false);
      }
      return;
    }
    const resolve = state._resolve;
    setState(null);
    resolve(true);
  }

  function handleCancel() {
    if (!state) return;
    const resolve = state._resolve;
    setState(null);
    resolve(false);
  }

  const node = (
    <ConfirmDialog
      open={Boolean(state)}
      title={state?.title || ''}
      body={state?.body || ''}
      confirmLabel={state?.confirmLabel}
      cancelLabel={state?.cancelLabel}
      danger={state?.danger}
      busy={busy}
      errorMessage={errorMessage}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
    />
  );

  return [node, ask];
}

const CONFIRM_DIALOG_CSS = `
  .confirm-dialog__backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1100;
    padding: 16px;
  }
  .confirm-dialog {
    background: var(--surface-0);
    border-radius: 12px;
    width: 100%;
    max-width: 480px;
    max-height: calc(100vh - 32px);
    overflow: auto;
    box-shadow: 0 24px 48px -16px rgba(15, 23, 42, 0.4);
    display: flex;
    flex-direction: column;
  }
  .confirm-dialog__head {
    padding: 14px 18px 4px;
    border-bottom: 1px solid var(--border-1);
  }
  .confirm-dialog__head h3 {
    margin: 0 0 8px;
    font-size: 16px;
  }
  .confirm-dialog__body {
    padding: 16px 18px;
    color: var(--text-2);
    font-size: 13.5px;
    line-height: 1.55;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .confirm-dialog__body p {
    margin: 0;
  }
  .confirm-dialog__error {
    margin-top: 4px;
    padding: 10px 12px;
    background: var(--danger-50, #fef2f2);
    color: var(--danger-700, #b91c1c);
    border-radius: 8px;
    font-size: 13px;
  }
  .confirm-dialog__foot {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    padding: 12px 18px;
    border-top: 1px solid var(--border-1);
    background: var(--surface-1);
  }
`;
